import React, { useState } from 'react';
import { usePIM } from '../../context/PIMContext';
import { Edit, Trash, Plus, Settings } from 'lucide-react';
import { GhostButton } from '../ui/GhostButton';
import { PrimaryButton } from '../ui/PrimaryButton';
import { Badge } from '../ui/Badge';
import { Input } from '../ui/Input';
import { Modal } from '../ui/Modal';
import { PropertyManagementModal } from '../modals/PropertyManagementModal';

import { useSortableData } from '../../hooks/useSortableData';
import { SortableHeader } from '../ui/SortableHeader';

export const PropertiesTab = () => {
    const { properties, availableProperties, addProperty, deleteProperty, updateProperty, canEditContent, canManageSettings, fetchProperties, productData, activeCharge } = usePIM();
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isManageModalOpen, setIsManageModalOpen] = useState(false);

    // Modal State
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedProperties, setSelectedProperties] = useState([]);

    // Sorting
    const { items: sortedProperties, requestSort, sortConfig } = useSortableData(properties);

    const handleUpdateValue = (id, newValue) => {
        updateProperty(id, { value: newValue });
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            e.target.blur();
        }
    };

    const togglePropertySelection = (propId) => {
        setSelectedProperties(prev =>
            prev.includes(propId)
                ? prev.filter(id => id !== propId)
                : [...prev, propId]
        );
    };

    const handleAddSelected = async () => {
        for (const propId of selectedProperties) {
            const prop = availableProperties.find(p => p.id === propId);
            if (prop) {
                await addProperty({
                    name: prop.name,
                    value: '',
                    group_name: prop.group_name,
                    group_color: prop.group_color,
                    is_feature: false,
                    is_public: 1
                });
            }
        }
        setIsAddModalOpen(false);
        setSelectedProperties([]);
        setSearchTerm('');

        // Refresh properties to ensure correct sorting from backend
        if (productData?.sku && activeCharge?.id) {
            await fetchProperties(productData.sku, activeCharge.id);
        }
    };

    // Filter available properties
    const filteredAvailableProperties = availableProperties
        .filter(ap => !properties.some(p => p.name.trim().toLowerCase() === ap.name.trim().toLowerCase()))
        .filter(ap => ap.name.toLowerCase().includes(searchTerm.toLowerCase()));

    const { items: sortedAvailableProperties, requestSort: requestSortAdd, sortConfig: sortConfigAdd } = useSortableData(filteredAvailableProperties);

    return (
        <div className="h-full flex flex-col">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h2 className="text-xl font-medium text-white">Properties</h2>
                    <p className="text-xs text-stone-500 mt-1">Manage product specifications.</p>
                </div>
                <div className="flex gap-3">
                    {canEditContent && <GhostButton onClick={() => setIsAddModalOpen(true)}><Plus size={16} /> Add Property</GhostButton>}
                    {canManageSettings && <GhostButton onClick={() => setIsManageModalOpen(true)}><Settings size={16} /> Manage Definitions</GhostButton>}
                </div>
            </div>

            <div className="bg-black/20 border border-white/5 rounded-xl overflow-hidden shadow-xl flex-1 flex flex-col">
                <div className="overflow-y-auto flex-1">
                    <table className="w-full text-left border-collapse">
                        <thead className="sticky top-0 z-10">
                            <tr>
                                <SortableHeader label="Group" sortKey="group_name" currentSort={sortConfig} onSort={requestSort} className="w-48" />
                                <SortableHeader label="Property" sortKey="name" currentSort={sortConfig} onSort={requestSort} className="w-1/4" />
                                <SortableHeader label="Value" sortKey="value" currentSort={sortConfig} onSort={requestSort} className="w-1/6" />
                                <SortableHeader label="Feature" sortKey="is_feature" currentSort={sortConfig} onSort={requestSort} align="center" className="w-24" />
                                <SortableHeader label="Hide" sortKey="is_public" currentSort={sortConfig} onSort={requestSort} align="center" className="w-24" />
                                <th className="bg-black/60 backdrop-blur-md text-stone-500 text-xs uppercase font-semibold px-4 py-3 border-b border-white/5 text-right w-24">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedProperties.map(prop => (
                                <tr key={`${prop.id}-${prop.value || ''}`} className="hover:bg-white/5 transition-colors border-b border-white/5 last:border-0 group">
                                    <td className="px-4 py-0.5">
                                        <Badge color={prop.group_color || 'gray'} size="sm">{prop.group_name || 'Uncategorized'}</Badge>
                                    </td>
                                    <td className="px-4 py-0.5 text-sm text-stone-300">{prop.name}</td>
                                    <td className="px-4 py-0.5">
                                        <div className="flex items-center gap-2">
                                            {prop.type_name === 'Boolean' ? (
                                                <input
                                                    type="checkbox"
                                                    defaultChecked={prop.value === 'true'}
                                                    onChange={(e) => handleUpdateValue(prop.id, e.target.checked ? 'true' : 'false')}
                                                    className="accent-stone-500 w-4 h-4 cursor-pointer"
                                                    style={{ colorScheme: 'dark' }}
                                                    disabled={!canEditContent}
                                                />
                                            ) : (
                                                <Input
                                                    defaultValue={prop.value}
                                                    onBlur={(e) => handleUpdateValue(prop.id, e.target.value)}
                                                    onKeyDown={handleKeyDown}
                                                    className="bg-transparent border-transparent hover:border-stone-700 focus:bg-transparent h-8 py-1 text-sm w-full"
                                                    disabled={!canEditContent}
                                                />
                                            )}
                                            {prop.unit && <span className="text-stone-500 text-xs whitespace-nowrap">{prop.unit}</span>}
                                        </div>
                                    </td>
                                    <td className="px-4 py-0.5 text-center">
                                        <input
                                            type="checkbox"
                                            defaultChecked={prop.is_feature}
                                            onChange={(e) => updateProperty(prop.id, { is_feature: e.target.checked })}
                                            className="accent-stone-500 w-4 h-4 cursor-pointer"
                                            style={{ colorScheme: 'dark' }}
                                            disabled={!canEditContent}
                                        />
                                    </td>
                                    <td className="px-4 py-0.5 text-center">
                                        <input
                                            type="checkbox"
                                            defaultChecked={prop.is_public === 0 || prop.is_public === false || prop.is_public === '0'}
                                            onChange={(e) => updateProperty(prop.id, { is_public: e.target.checked ? 0 : 1 })}
                                            className="accent-stone-500 w-4 h-4 cursor-pointer"
                                            style={{ colorScheme: 'dark' }}
                                            disabled={!canEditContent}
                                            title="Wenn markiert, wird diese Eigenschaft auf der Webseite versteckt."
                                        />
                                    </td>
                                    <td className="px-4 py-0.5 text-right">
                                        <div className="flex justify-end gap-2">
                                            {canEditContent && (
                                                <button
                                                    onClick={() => deleteProperty(prop.id)}
                                                    className="w-7 h-7 rounded border border-stone-600 text-stone-400 hover:text-red-400 hover:border-red-900/50 hover:bg-red-900/10 flex items-center justify-center transition"
                                                >
                                                    <Trash size={14} />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Add Modal - Smart Selection */}
            <Modal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} title="Add Properties" maxWidth="max-w-4xl">
                <div className="space-y-4 flex flex-col h-[700px]">
                    <Input
                        placeholder="Search available properties..."
                        className="bg-stone-800 border-stone-600"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />

                    <div className="flex-1 overflow-hidden border border-stone-700 rounded-lg flex flex-col">
                        <div className="grid grid-cols-[auto_1fr_1fr_auto] gap-4 px-3 py-2 bg-black/60 backdrop-blur-md text-stone-500 text-xs uppercase font-semibold border-b border-white/5 sticky top-0 z-10">
                            <div className="w-4"></div>
                            <SortableHeader label="Property" sortKey="name" currentSort={sortConfigAdd} onSort={requestSortAdd} as="div" />
                            <SortableHeader label="Group" sortKey="group_name" currentSort={sortConfigAdd} onSort={requestSortAdd} className="text-left" as="div" />
                            <SortableHeader label="Type" sortKey="type_name" currentSort={sortConfigAdd} onSort={requestSortAdd} className="text-right" as="div" />
                        </div>
                        <div className="overflow-y-auto flex-1 p-1 space-y-1">
                            {sortedAvailableProperties.map(ap => (
                                <div
                                    key={ap.id}
                                    onClick={() => togglePropertySelection(ap.id)}
                                    className={`grid grid-cols-[auto_1fr_1fr_auto] gap-4 items-center px-3 py-1 border rounded cursor-pointer transition select-none ${selectedProperties.includes(ap.id) ? 'bg-blue-900/20 border-blue-500/50' : 'bg-stone-800/30 border-stone-700 hover:bg-stone-800'}`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedProperties.includes(ap.id)}
                                        onChange={() => { }} // Handled by parent div click
                                        className="accent-stone-500 w-4 h-4 cursor-pointer"
                                    />
                                    <div className="text-sm text-stone-200">{ap.name}</div>
                                    <div className="text-left"><Badge color={ap.group_color} size="sm">{ap.group_name}</Badge></div>
                                    <div className="text-right text-xs text-stone-400">{ap.type_name}</div>
                                </div>
                            ))}
                            {sortedAvailableProperties.length === 0 && (
                                <div className="text-center text-stone-500 py-4">No new properties available.</div>
                            )}
                        </div>
                    </div>

                    <div className="flex justify-end gap-2 pt-4 border-t border-stone-700">
                        <GhostButton onClick={() => setIsAddModalOpen(false)}>Cancel</GhostButton>
                        <PrimaryButton onClick={handleAddSelected}>Add Selected ({selectedProperties.length})</PrimaryButton>
                    </div>
                </div>
            </Modal>

            {/* Management Modals */}
            <PropertyManagementModal
                isOpen={isManageModalOpen}
                onClose={() => {
                    setIsManageModalOpen(false);
                    if (productData?.sku && activeCharge?.id) {
                        fetchProperties(productData.sku, activeCharge.id);
                    }
                }}
            />
        </div>
    );
};
